import { Pencil, Plus, Trash2 } from "lucide-react";
import { useDelete, useListContext, useRecordContext } from "ra-core";
import { useState } from "react";
import { CreateButton } from "@/components/admin/create-button";
import { DataTable } from "@/components/admin/data-table";
import { List } from "@/components/admin/list";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import { TopToolbar } from "../layout/TopToolbar";
import type { MessageTemplate } from "../types";
import { MessageTemplateCreate } from "./MessageTemplateCreate";
import { MessageTemplateEdit } from "./MessageTemplateEdit";

const MessageTemplateListActions = () => {
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <>
      <TopToolbar>
        <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" />
          New template
        </Button>
      </TopToolbar>
      <MessageTemplateCreate
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </>
  );
};

const ChannelField = () => {
  const record = useRecordContext<MessageTemplate>();
  if (!record) return null;
  return (
    <Badge
      variant="outline"
      className={
        record.channel === "whatsapp"
          ? "border-green-500 text-green-700 dark:text-green-400"
          : "border-blue-500 text-blue-700 dark:text-blue-400"
      }
    >
      {record.channel === "whatsapp" ? "WhatsApp" : "SMS"}
    </Badge>
  );
};

const BodyField = () => {
  const record = useRecordContext<MessageTemplate>();
  if (!record) return null;
  const truncated =
    record.body.length > 80
      ? record.body.substring(0, 80) + "..."
      : record.body;
  return <span className="text-sm text-muted-foreground">{truncated}</span>;
};

const ActionsField = () => {
  const record = useRecordContext<MessageTemplate>();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOne] = useDelete();
  const { refetch } = useListContext();

  if (!record) return null;

  const handleDelete = async () => {
    await deleteOne("messageTemplates", {
      id: record.id,
      previousData: record,
    });
    refetch();
  };

  return (
    <div className="flex gap-1 justify-end">
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={(e) => {
          e.stopPropagation();
          setEditOpen(true);
        }}
      >
        <Pencil className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-destructive"
        onClick={(e) => {
          e.stopPropagation();
          handleDelete();
        }}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
      <MessageTemplateEdit
        template={record}
        open={editOpen}
        onClose={() => setEditOpen(false)}
      />
    </div>
  );
};

export function MessageTemplateList() {
  return (
    <List
      title={false}
      actions={<MessageTemplateListActions />}
      sort={{ field: "created_at", order: "DESC" }}
    >
      <DataTable>
        <DataTable.Col source="name" label="Name" />
        <DataTable.Col label="Message">
          <BodyField />
        </DataTable.Col>
        <DataTable.Col label="Channel">
          <ChannelField />
        </DataTable.Col>
        <DataTable.Col label={false}>
          <ActionsField />
        </DataTable.Col>
      </DataTable>
    </List>
  );
}
